<script lang="ts">
  import { generateUniqueElementId } from '@athena-lib/unique-element-id';
  import { KeyboardArrowDown } from '@athena-lib/Icons';
  import clsx from 'clsx';
  import { type DropdownProps } from './types';

  let {
    id: providedId,
    class: className,
    innerClass,
    value = $bindable(),
    label,
    options,
    disabled,
    placeholder,
    error,
    helpText,
    ...restProps
  }: DropdownProps = $props();

  const id = $derived(providedId ?? generateUniqueElementId());
  const message = $derived(error ?? helpText);
  const displayValue = $derived.by(() => {
    const option = options.find((opt) => opt.value === value);
    return option?.label ?? placeholder;
  });
</script>

<div class={clsx(className, 'flex w-full flex-col')}>
  <div
    class={clsx(
      'relative',
      'h-12',
      'rounded',
      'bg-white',
      'border',
      'has-[:focus]:border-st-yellow-500 has-[:focus]:ring has-[:focus]:ring-st-yellow-200',
      'inline-block',
      !disabled && 'border-st-gray-700',
      disabled && 'border-st-gray-400 bg-st-gray-100',
      !error &&
        'has-[:focus]:border-st-yellow-500 has-[:focus]:ring has-[:focus]:ring-st-yellow-200',
      error && 'border-st-red-500 ring ring-st-red-200',
    )}
  >
    <div class={clsx('relative flex h-full px-4 pt-4', innerClass)}>
      <label
        for={id}
        class={clsx(
          'absolute',
          'top-1',
          'text-xs',
          'font-normal',
          'tracking-wide',
          disabled && 'text-st-gray-500',
        )}>{label}</label
      >
      <span class={clsx('align-middle text-base', disabled && 'text-st-gray-500')}>
        {displayValue}
      </span>
      <span
        class={clsx(disabled && 'text-st-gray-500', 'absolute right-2.5 top-2.5')}
        aria-hidden="true"
      >
        <KeyboardArrowDown sizeClass="w-6 h-6" />
      </span>
    </div>
    <select
      {...restProps}
      {id}
      {disabled}
      bind:value
      class="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      aria-invalid={!!error}
      aria-describedby={message ? `${id}-message` : undefined}
    >
      {#if placeholder}
        <option value="" selected={value === ''}>{placeholder}</option>
      {/if}
      {#each options as option (option.value)}
        <option selected={option.value === value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      {/each}
    </select>
  </div>
  {#if message}
    <span
      id={`${id}-message`}
      class={clsx('mt-1', 'text-sm', 'font-normal', 'text-st-gray-800', error && 'text-st-red-500')}
    >
      {message}
    </span>
  {/if}
</div>
